<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Alert from '@/components/Atoms/Alert.vue'

/**
 * Name, group (`row`) and transition of one graphic. Positioning happens on
 * the Preview canvas; this sheet is for the rest.
 */
const props = defineProps({
	modelValue: { type: Boolean, default: false },
	overlay: { type: Object, default: null },
	/** Existing row names, for the picker. */
	rows: { type: Array, default: () => [] },
	saving: { type: Boolean, default: false },
	error: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'save'])

const NEW_ROW = '__new__'
const name = ref('')
const row = ref('')
const newRow = ref('')
const type = ref('Cut')
const duration = ref(300)
const nameInput = ref(null)

const open = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) })

watch(() => [props.modelValue, props.overlay], async ([visible]) => {
	if (!visible || !props.overlay) return
	name.value = props.overlay.name || ''
	row.value = props.overlay.row || 'Graphics'
	newRow.value = ''
	type.value = props.overlay.transition?.type === 'Fade' ? 'Fade' : 'Cut'
	duration.value = parseInt(props.overlay.transition?.duration, 10) || 300
	await nextTick()
	nameInput.value?.focus?.()
}, { immediate: true })

const rowOptions = computed(() => {
	const names = Array.from(new Set([...(props.rows || []), props.overlay?.row].filter(Boolean)))
	return [...names.map((n) => ({ label: n, value: n })), { label: 'New group…', value: NEW_ROW }]
})
const resolvedRow = computed(() => (row.value === NEW_ROW ? newRow.value.trim() : row.value))
const valid = computed(() => !!name.value.trim() && !!resolvedRow.value)

const save = () => {
	if (!valid.value || props.saving) return
	emit('save', {
		name: name.value.trim(),
		row: resolvedRow.value,
		transition: { type: type.value, duration: type.value === 'Cut' ? 0 : Math.max(50, Math.min(10000, parseInt(duration.value, 10) || 300)) },
	})
}
</script>

<template>
	<ModalBox v-model="open" title="Graphic settings" size="sm" placement="center" mobile-placement="bottom" header-class="!px-5 !pt-5 !pb-3">
		<form class="flex flex-col gap-3.5 px-5" @submit.prevent="save">
			<Alert v-if="error" type="error" class="!mb-0">{{ error }}</Alert>
			<FormInput ref="nameInput" v-model="name" label="Name" placeholder="e.g. Sponsor logo" :maxlength="120" data-testid="graphic-name" />
			<div>
				<FormSelect v-model="row" label="Group" :options="rowOptions" data-testid="graphic-row" />
				<FormInput v-if="row === NEW_ROW" v-model="newRow" class="mt-2" placeholder="Group name, e.g. Lower thirds" :maxlength="60" data-testid="graphic-new-row" />
			</div>
			<div>
				<span class="mb-2 block text-tiny font-medium text-surface-text-1">Transition when it goes on / off air</span>
				<TabGroup type="segmented" size="sm" :cols="2">
					<TabItem v-for="t in ['Cut', 'Fade']" :key="t" :is-active="type === t" :aria-pressed="type === t" @click="type = t">{{ t }}</TabItem>
				</TabGroup>
				<FormInput
					v-model="duration"
					type="number"
					size="sm"
					class="mt-2"
					label="Duration (ms)"
					:disabled="type === 'Cut'"
					:helper="type === 'Cut' ? 'Cut switches instantly.' : '50 – 10000 ms'"
					data-testid="graphic-duration"
				/>
			</div>
		</form>
		<template #footer>
			<div class="flex flex-col-reverse gap-2 px-5 pb-5 pt-4 sm:flex-row sm:justify-end">
				<FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="saving" @click="open = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!valid" :is-loading="saving" data-testid="graphic-save" @click="save">Save</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
